// CandidatesPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';
import { Check2, ChevronDown } from 'react-bootstrap-icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { STATE_OPTIONS, useHomeLocation } from '../../utils/homeLocation';
import StateCandidates from './StateCandidates';
import LocalCandidates from './LocalCandidates';
import PartiesOnBallot from '../elections/parties/PartiesOnBallot';

import './MemberDetailPage.css';
import './StateCandidates.css';
import '../pages/Representatives.css';

/**
 * CandidatesPage
 *
 * Candidate browser, mounted at both:
 *
 *   /us/candidates              every candidate for the selected level
 *   /us/candidates/:stateAbbr   narrowed to one state
 *
 * One component serves both so there is a single place the filters live. The
 * state is carried in the path rather than in component state, so a filtered
 * view is a real address someone can paste or share - picking a state
 * navigates rather than setting state.
 *
 * Level, office and county are toggles and stay out of the path: they are views
 * of the same place, and several URLs for one page is a cost with no benefit.
 * County additionally arrives through navigation state when the Representatives
 * page sends someone here, so a user who has already told the app where they
 * live does not have to say it twice.
 *
 * Two sources feed this page, split by level rather than blended:
 *
 *   Federal   api.govglance.org, keyed on the FEC candidate ID, with a detail
 *             page behind every row.
 *   Below     api2.govglance.org, no FEC ID, no detail page, rows expanding in
 *             place with whatever contact details the state published.
 *
 * They are not merged into one list. The two describe different things -- a
 * federal filing carries campaign finance, a county filing carries a phone
 * number and often not much else -- and a single list would have to render the
 * union of two shapes, most of it empty in either direction.
 *
 * Once a state is chosen, the parties on its next ballot sit above the list,
 * each one a way into its party page. It does not filter the list below: a
 * party filter here would have to be applied to two differently shaped
 * sources, and the party page already lists who the party is running in this
 * state, from the certified ballot.
 *
 * Presidential candidates are not filed under a state, so selecting that office
 * drops the state filter entirely and returns to /us/candidates. Every other
 * level requires a state: api2 will not answer a candidate query without one.
 *
 * The state list is STATE_OPTIONS, the same source LocationPicker uses, so the
 * flags and names match the picker a user just came from rather than being a
 * second list that can drift out of step.
 */

const LEVELS = [
  { id: 'federal', labelKey: 'common:federal', fallback: 'Federal' },
  { id: 'state', labelKey: 'common:state', fallback: 'State' },
  { id: 'county', labelKey: 'common:county', fallback: 'County' },
  { id: 'city', labelKey: 'common:city', fallback: 'City' },
];

const OFFICES = [
  { id: 'House', labelKey: 'common:usHouse', fallback: 'U.S. House' },
  { id: 'Senate', labelKey: 'common:usSenate', fallback: 'U.S. Senate' },
  { id: 'President', labelKey: 'common:president', fallback: 'President' },
];

function CandidatesPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { stateAbbr } = useParams();
  const { location: home } = useHomeLocation();

  // Sent by CandidatesLinkButton. County is not in the path, so this is how a
  // user arriving from their own representatives lands on their own county
  // rather than on a state-wide list they then have to narrow by hand.
  const handoff = routerLocation.state || {};

  const [level, setLevel] = useState(handoff.level || 'federal');
  const [office, setOffice] = useState('House');
  const [county, setCounty] = useState(handoff.county || home?.county || '');
  const [countyOptions, setCountyOptions] = useState([]);
  const [stateListOpen, setStateListOpen] = useState(false);
  const [countyListOpen, setCountyListOpen] = useState(false);

  const requested = String(stateAbbr || '').toUpperCase();

  const selectedState = useMemo(
    () => STATE_OPTIONS.find((option) => option.abbr === requested) || null,
    [requested]
  );

  // An unrecognized code is treated as no filter rather than an error page: the
  // list is still useful, and the picker shows what was actually applied.
  const activeState = selectedState?.abbr || '';

  const isFederal = level === 'federal';
  const isPresident = isFederal && office === 'President';

  // A county filter is only meaningful for a contest that belongs to a county.
  // Iowa files its statewide races into each county's own candidate PDF, so a
  // state-level row does carry a county_name -- but a statewide contest is not
  // a county contest however it was published, and filtering one by county is
  // rejected outright.
  const countyFilterApplies = !isFederal && level !== 'state';

  // Rendered wherever it applies, not only at county level. City contests
  // belong to a county too, and the notice about a cut list points at this
  // control -- so a level that can be narrowed must show the way to narrow it.
  const showCountyPicker =
    countyFilterApplies && Boolean(activeState) && countyOptions.length > 0;

  // The counties a state actually publishes candidates for is not a list that
  // exists anywhere to be fetched -- it is whatever came back. So it is
  // collected from an unfiltered answer and then held while the user filters
  // with it, which is why a county selection does not narrow the picker that
  // produced it down to one entry.
  //
  // Remembered per state and level, because that same guard makes the list
  // unrecoverable once a county is applied: leaving county for city and coming
  // back cleared the options, and every answer from then on was filtered, so
  // there was nothing to rebuild them from and the picker stayed gone until the
  // state was reset. Kept in a ref rather than state -- writing it never needs
  // to trigger a render of its own.
  const countyCache = useRef(new Map());
  const facetKey = `${activeState}:${level}`;

  const handleFacets = useCallback(
    ({ counties }) => {
      if (county || !counties.length) return;
      countyCache.current.set(facetKey, counties);
      setCountyOptions(counties);
    },
    [county, facetKey]
  );

  // Iowa's statewide rows carry a county, so a level change out of county or
  // city leaves a filter that the next request cannot apply.
  useEffect(() => {
    if (level === 'federal' || level === 'state') setCounty('');
  }, [level]);

  // Restored rather than cleared. A county belongs to a state, so a state
  // change genuinely invalidates the list -- but returning to a level already
  // visited should show what it showed before, not an empty control.
  useEffect(() => {
    setCountyOptions(countyCache.current.get(facetKey) || []);
  }, [facetKey]);

  // The cache is per state, so a state change empties it outright.
  useEffect(() => {
    countyCache.current.clear();
  }, [activeState]);

  const handleStateSelect = (abbr) => {
    setStateListOpen(false);
    setCounty('');
    navigate(abbr ? `/us/candidates/${abbr}` : '/us/candidates');
  };

  const handleLevelSelect = (id) => {
    setLevel(id);
    // Below federal level api2 requires a state, so an unfiltered view has
    // nothing to request. Fall back to the saved one rather than showing an
    // empty page next to a picker.
    if (id !== 'federal' && !activeState && home?.stateAbbr) {
      navigate(`/us/candidates/${home.stateAbbr}`, { replace: true });
    }
  };

  const handleOfficeSelect = (id) => {
    setOffice(id);
    // Presidential candidates carry no state, so a state-filtered URL would
    // describe a filter that is not being applied.
    if (id === 'President' && activeState) {
      navigate('/us/candidates');
    }
  };

  const heading = selectedState
    ? t('common:stateCandidatesTitle', '{{state}} candidates', { state: selectedState.name })
    : t('common:allCandidatesTitle', 'Candidates');

  const renderList = () => {
    if (isFederal) {
      return (
        // Keyed on the pair so changing either filter remounts the list rather
        // than briefly showing the previous selection's rows under the new
        // heading.
        <StateCandidates
          key={`fed-${activeState}-${office}`}
          stateAbbreviation={isPresident ? '' : activeState}
          candidateType={office}
        />
      );
    }

    if (!activeState) {
      return (
        <p className="member-detail-text candidates-state-prompt">
          {t('common:pickStateForCandidates', 'Choose a state to see who is running.')}
        </p>
      );
    }

    return (
      <LocalCandidates
        key={`local-${activeState}-${level}-${county}`}
        stateAbbr={activeState}
        level={level}
        county={countyFilterApplies ? county : ''}
        onFacets={handleFacets}
        countyFilterAvailable={showCountyPicker}
      />
    );
  };

  return (
    <div id="feedDisplay" className="member-detail-page">
      <div className="member-page-content">
        <div className="member-section-heading member-section-heading--center">
          {heading}
        </div>

        <div className="member-tabs member-tabs--center" role="tablist">
          {LEVELS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={level === option.id}
              className={`member-tab ${level === option.id ? 'member-tab--active' : ''}`}
              onClick={() => handleLevelSelect(option.id)}
            >
              {t(option.labelKey, option.fallback)}
            </button>
          ))}
        </div>

        {/* The office split is a federal idea. State, county and city filings
            name hundreds of different offices, so a fixed set of buttons would
            be wrong everywhere below Congress -- those levels are searched
            instead. */}
        {isFederal && (
          <div className="member-tabs member-tabs--center member-tabs--secondary" role="tablist">
            {OFFICES.map((option) => (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={office === option.id}
                className={`member-tab ${office === option.id ? 'member-tab--active' : ''}`}
                onClick={() => handleOfficeSelect(option.id)}
              >
                {t(option.labelKey, option.fallback)}
              </button>
            ))}
          </div>
        )}

        {!isPresident && (
          <div className="candidates-state-picker">
            {/* A custom listbox rather than <select>, matching LocationPicker:
                native selects open an OS picker that ignores every font rule the
                app sets and cannot carry a flag. */}
            <button
              type="button"
              className="location-picker-input location-picker-dropdown-toggle"
              aria-haspopup="listbox"
              aria-expanded={stateListOpen}
              onClick={() => setStateListOpen((open) => !open)}
            >
              <span className="location-picker-dropdown-value">
                <span className="location-picker-flag-slot">
                  {selectedState?.flag && (
                    <Image src={selectedState.flag} alt="" className="location-picker-flag" />
                  )}
                </span>
                <span className={selectedState ? '' : 'location-picker-placeholder'}>
                  {selectedState ? selectedState.name : t('common:allStates', 'All states')}
                </span>
              </span>
              <ChevronDown size={14} />
            </button>

            {stateListOpen && (
              <ul className="location-picker-dropdown-menu" role="listbox">
                {/* Only offered where it means something. Below federal level a
                    state is required, so an "all states" row would be a filter
                    the page cannot apply. */}
                {isFederal && (
                  <li>
                    <button
                      type="button"
                      role="option"
                      aria-selected={!activeState}
                      className={`location-picker-dropdown-option ${
                        !activeState ? 'location-picker-dropdown-option-selected' : ''
                      }`}
                      onClick={() => handleStateSelect('')}
                    >
                      <span className="location-picker-dropdown-value">
                        {/* Empty slot keeps this row's text aligned with the
                            flagged rows under it. */}
                        <span className="location-picker-flag-slot" />
                        <span>{t('common:allStates', 'All states')}</span>
                      </span>
                      {!activeState && <Check2 size={16} />}
                    </button>
                  </li>
                )}

                {STATE_OPTIONS.map((option) => {
                  const selected = option.abbr === activeState;
                  return (
                    <li key={option.key}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`location-picker-dropdown-option ${
                          selected ? 'location-picker-dropdown-option-selected' : ''
                        }`}
                        onClick={() => handleStateSelect(option.abbr)}
                      >
                        <span className="location-picker-dropdown-value">
                          {/* Fixed-width slot rather than a bare image: DC and
                              the territories have no flag asset, and without a
                              reserved space their names would sit out of line
                              with everything else. */}
                          <span className="location-picker-flag-slot">
                            {option.flag && (
                              <Image
                                src={option.flag}
                                alt=""
                                className="location-picker-flag"
                                onError={(e) => {
                                  e.currentTarget.style.visibility = 'hidden';
                                }}
                              />
                            )}
                          </span>
                          <span>{option.name}</span>
                        </span>
                        {selected && <Check2 size={16} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* County filtering is closer to required than optional in the deep
            states: Indiana files 8,670 non-federal candidates and Louisiana
            4,791, so a state-wide list there is cut long before it is read. */}
        {showCountyPicker && (
          <div className="candidates-state-picker">
            <button
              type="button"
              className="location-picker-input location-picker-dropdown-toggle"
              aria-haspopup="listbox"
              aria-expanded={countyListOpen}
              onClick={() => setCountyListOpen((open) => !open)}
            >
              <span className="location-picker-dropdown-value">
                <span className={county ? '' : 'location-picker-placeholder'}>
                  {county || t('common:allCounties', 'All counties')}
                </span>
              </span>
              <ChevronDown size={14} />
            </button>

            {countyListOpen && (
              <ul className="location-picker-dropdown-menu" role="listbox">
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={!county}
                    className={`location-picker-dropdown-option ${
                      !county ? 'location-picker-dropdown-option-selected' : ''
                    }`}
                    onClick={() => {
                      setCounty('');
                      setCountyListOpen(false);
                    }}
                  >
                    <span className="location-picker-dropdown-value">
                      <span>{t('common:allCounties', 'All counties')}</span>
                    </span>
                    {!county && <Check2 size={16} />}
                  </button>
                </li>

                {countyOptions.map((name) => {
                  const selected = name === county;
                  return (
                    <li key={name}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`location-picker-dropdown-option ${
                          selected ? 'location-picker-dropdown-option-selected' : ''
                        }`}
                        onClick={() => {
                          setCounty(name);
                          setCountyListOpen(false);
                        }}
                      >
                        <span className="location-picker-dropdown-value">
                          <span>{name}</span>
                        </span>
                        {selected && <Check2 size={16} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Hidden for President: that office drops the state, and a state's
            ballot parties under a national list would describe a filter that is
            not applied. Keyed so a state change shows skeletons, not the last
            state's parties. */}
        {activeState && !isPresident && (
          <PartiesOnBallot key={activeState} stateAbbr={activeState} showHint />
        )}

        {renderList()}
      </div>

      <style>{`
        /* Match the Glance column width on the homepage so cards read as cards
           rather than full-width bars on wide desktop screens. */
        .member-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }

        .candidates-state-picker {
          position: relative;
          margin-bottom: 1rem;
        }

        /* Fifty-plus entries need to scroll rather than run off the viewport.
           The menu expands in flow, as it does in LocationPicker, so it is not
           clipped on short screens. Counties reuse it: Texas has 254. */
        .candidates-state-picker .location-picker-dropdown-menu {
          max-height: 55vh;
          overflow-y: auto;
        }

        /* The office row sits under the level row and answers a narrower
           question, so it is quieter rather than a second row of equals. */
        .member-tabs--secondary {
          margin-top: -0.25rem;
          margin-bottom: 0.75rem;
          font-size: 0.9em;
        }

        .candidates-state-prompt {
          text-align: center;
          padding: 1.5rem 1rem;
        }
      `}</style>
    </div>
  );
}

export default CandidatesPage;